import React from 'react';
import apiManage from '../../request';
import { getRBPStringId } from '../main/main-page-states';
import { parseRestaurantStaticBundled, type RestaurantStaticBundledView } from '../../utils/restaurant-static-bundled';

/**
 * 「首页与营销」子卡片共享的 bundled 数据与刷新方法（由 Provider 在挂载时拉取一次）。
 */
export type FirstScreenBundledContextValue = {
    /** 解析后的资源图集合 */
    bundled: RestaurantStaticBundledView;
    /** 重新请求 bundled（上传/删除成功后调用） */
    reloadBundled: () => Promise<void>;
    /** 用于图片 URL 缓存破坏的时间戳 */
    bustVersion: number;
    /** 首次挂载时 `reloadBundled` 是否已完成（用于子功能/我的餐厅共用的首屏骨架） */
    bundledReady: boolean;
};

/** 首页与营销各子组件通过 `contextType` 消费 */
export const FirstScreenBundledContext = React.createContext<FirstScreenBundledContextValue | null>(null);

type ProviderProps = {
    /** 子节点：首页栅格内的各 Card */
    children: React.ReactNode;
};

type ProviderState = {
    /** 解析后的 bundled */
    bundled: RestaurantStaticBundledView;
    /** 缓存破坏版本 */
    bustVersion: number;
    /** 首包 bundled 请求是否已结束 */
    initialBundledLoadDone: boolean;
};

/**
 * 在「首页与营销」页面包一层 Context，统一拉取 `bundled-image-urls` 供活动公告、环境图、就餐指南、推荐菜等展示。
 */
export class FirstScreenBundledProvider extends React.Component<ProviderProps, ProviderState> {
    /** Provider 内部 state 初始值 */
    public state: Readonly<ProviderState> = {
        bundled: parseRestaurantStaticBundled(null),
        bustVersion: 0,
        initialBundledLoadDone: false,
    };

    /**
     * 请求当前餐厅的 bundled 并写入 state。
     */
    public reloadBundled = async (): Promise<void> => {
        const stringId = getRBPStringId();
        const raw = await apiManage.get_restaurant_static_bundled_image_urls(stringId);
        this.setState({
            bundled: parseRestaurantStaticBundled(raw),
            bustVersion: Date.now(),
        });
    };

    /**
     * 挂载后拉取 bundled；结束后再开放子页面实内容，避免与骨架不同步。
     */
    public async componentDidMount(): Promise<void> {
        try {
            await this.reloadBundled();
        } finally {
            this.setState({ initialBundledLoadDone: true });
        }
    }

    /**
     * 渲染 Provider。
     */
    public render(): React.ReactNode {
        const value: FirstScreenBundledContextValue = {
            bundled: this.state.bundled,
            reloadBundled: this.reloadBundled,
            bustVersion: this.state.bustVersion,
            bundledReady: this.state.initialBundledLoadDone,
        };
        return <FirstScreenBundledContext.Provider value={value}>{this.props.children}</FirstScreenBundledContext.Provider>;
    }
}
